/**
 * Phase 15.5 P1 — Submit Beat Page
 *
 * Producers upload beats here. Audio + cover go through the existing
 * Cloudinary `/upload/audio` and `/upload/image` endpoints (same as
 * track uploads), then we POST the metadata to `/api/beats`.
 *
 * Fields: title, BPM, key, genre, mood, tags, price, license_type,
 * contact channels (email / Instagram / Discord / Website).
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const LICENSES = ["Free", "Lease", "Premium", "Exclusive"];
const KEYS = ["", "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
              "Cm", "C#m", "Dm", "D#m", "Em", "Fm", "F#m", "Gm", "G#m", "Am", "A#m", "Bm"];

// Phase 15.6Q — Multi-tier monetization defaults
const TIER_PRESET = [
  { label: "Free Preview", price_usd: 0, terms: "Stream only — no download, no commercial use.", enabled: true },
  { label: "Basic Lease", price_usd: 30, terms: "MP3 download. Up to 2,000 streams. Non-exclusive.", enabled: true },
  { label: "Premium Lease", price_usd: 80, terms: "MP3 + WAV. Up to 50,000 streams. Non-exclusive.", enabled: false },
  { label: "Unlimited Lease", price_usd: 150, terms: "MP3 + WAV + trackouts. Unlimited streams. Non-exclusive.", enabled: false },
  { label: "Exclusive Rights", price_usd: 500, terms: "Full ownership transfer. Beat removed from marketplace.", enabled: false },
];

const SubmitBeatPage = () => {
  const nav = useNavigate();
  const { beatId } = useParams();
  const isEdit = !!beatId;
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [loadingBeat, setLoadingBeat] = useState(isEdit);

  const [form, setForm] = useState({
    title: "",
    bpm: "",
    key: "",
    genre: "",
    mood: "",
    tags: "",
    price: "",
    license_type: "Lease",
    contact_email: "",
    contact_instagram: "",
    contact_discord: "",
    contact_website: "",
    // Phase 19.0 — Download controls
    download_mode: "paid",
    require_account_to_download: true,
  });
  const [files, setFiles] = useState({ audio: null, cover: null });
  const [uploading, setUploading] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  // Phase 15.6Q — multi-tier pricing
  const [useTiers, setUseTiers] = useState(false);
  const [tiers, setTiers] = useState(TIER_PRESET);

  const updateTier = (label, patch) =>
    setTiers((prev) => prev.map((t) => (t.label === label ? { ...t, ...patch } : t)));

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setAuthError("login_required"));
  }, []);

  // Phase 17.1 — Edit mode: pre-populate form from the existing beat.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/beats/${beatId}`);
        const b = r?.data?.beat || r?.data;
        if (!b || cancelled) return;
        setForm({
          title: b.title || "",
          bpm: b.bpm ?? "",
          key: b.key || "",
          genre: b.genre || "",
          mood: b.mood || "",
          tags: Array.isArray(b.tags) ? b.tags.join(", ") : "",
          price: b.price ?? "",
          license_type: b.license_type || "Lease",
          contact_email:     b.contact_email || "",
          contact_instagram: b.contact_instagram || "",
          contact_discord:   b.contact_discord || "",
          contact_website:   b.contact_website || "",
          download_mode: b.download_mode || "paid",
          require_account_to_download: b.require_account_to_download !== false,
        });
        if (b.cover_url) setCoverPreview(b.cover_url);
        if (Array.isArray(b.tiers) && b.tiers.length) {
          setUseTiers(true);
          setTiers((prev) => prev.map((t) => {
            const match = b.tiers.find((bt) => bt.label === t.label);
            return match ? { ...t, ...match, enabled: true } : { ...t, enabled: false };
          }));
        }
      } catch (e) {
        toast.error("Couldn't load beat for editing");
      } finally {
        if (!cancelled) setLoadingBeat(false);
      }
    })();
    return () => { cancelled = true; };
  }, [beatId, isEdit]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onCover = (f) => {
    setFiles((p) => ({ ...p, cover: f }));
    if (f) {
      const reader = new FileReader();
      reader.onload = () => setCoverPreview(reader.result);
      reader.readAsDataURL(f);
    } else setCoverPreview(null);
  };

  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    const endpoint = kind === "audio" ? "/upload/audio" : "/upload/image";
    const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data?.url || r.data?.secure_url || r.data?.audio_url || r.data?.cover_url;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!isEdit && !files.audio) { toast.error("Audio file is required"); return; }

    setUploading(true);
    try {
      let audio_url = null;
      if (files.audio) {
        audio_url = await uploadFile(files.audio, "audio");
      }
      let cover_url = null;
      if (files.cover) cover_url = await uploadFile(files.cover, "image");

      const payload = {
        title: form.title.trim(),
        bpm: form.bpm ? Number(form.bpm) : null,
        key: form.key || null,
        genre: form.genre || null,
        mood: form.mood || null,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        price: form.price ? Number(form.price) : 0,
        license_type: form.license_type,
        tiers: useTiers ? tiers.filter((t) => t.enabled) : [],
        contact_email:     form.contact_email || null,
        contact_instagram: form.contact_instagram || null,
        contact_discord:   form.contact_discord || null,
        contact_website:   form.contact_website || null,
        download_mode: form.download_mode || "paid",
        require_account_to_download: !!form.require_account_to_download,
      };
      // Only include URLs when we have fresh ones — preserve existing on edit.
      if (audio_url) payload.audio_url = audio_url;
      if (cover_url) payload.cover_url = cover_url;

      let r;
      if (isEdit) {
        r = await api.put(`/beats/${beatId}`, payload);
        toast.success("✓ Beat updated");
        nav(`/beats/${beatId}`);
      } else {
        r = await api.post("/beats", payload);
        toast.success("🎉 Beat published to the marketplace!");
        const bid = r.data?.beat?.beat_id;
        // Phase 17.1 — Land producer on their dashboard (or the new beat
        // detail page) instead of bouncing to the artist profile which
        // previously crashed on the Beats tab.
        if (bid) nav(`/beats/${bid}`);
        else nav("/producer");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      let msg;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = d[0].msg;
      else if (d?.message) msg = d.message;
      else if (d?.msg) msg = d.msg;
      else msg = "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0a0a0e] text-white">
        <Card className="p-6 max-w-md text-center bg-white/[0.03] border-white/10">
          <div className="text-5xl mb-3">🎹</div>
          <h2 className="text-xl font-black mb-2">Log in to upload a beat</h2>
          <p className="text-sm text-gray-400 mb-4">
            We&apos;ll bring you straight back here after you sign in.
          </p>
          <Button
            onClick={() => nav("/login?next=/submit-beat")}
            className="bg-pink-500 hover:bg-pink-400 text-white font-bold"
            data-testid="submit-beat-login-cta"
          >
            Log in to continue →
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0e] p-4 sm:p-6 text-white" data-testid="submit-beat-page">
      <div className="max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => nav(isEdit ? `/beats/${beatId}` : -1)}
          className="text-xs text-pink-300 hover:text-pink-200 mb-3 inline-flex items-center gap-1"
          data-testid="submit-beat-back-btn"
        >
          ← {isEdit ? "Back to beat" : "Back"}
        </button>
        <h1 className="text-3xl font-black mb-1" data-testid="submit-beat-title">
          🎹 {isEdit ? "Edit Beat" : "Upload a Beat"}
        </h1>
        <p className="text-sm text-gray-400 mb-5">
          Upload your beat to the RGM Studio marketplace. Set your price, license, and terms.
          Artists can lease through secure checkout, and producers earn from sales and support.
        </p>

        {loadingBeat ? (
          <p className="text-pink-300 text-sm">Loading beat…</p>
        ) : (
        <form onSubmit={submit} className="space-y-5">
          <Card className="p-4 bg-white/[0.02] border-white/10 space-y-3">
            <h2 className="text-sm font-black uppercase tracking-wider">Files</h2>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Audio (mp3 / wav)</label>
              <input
                type="file" accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,audio/*"
                onChange={(e) => setFiles((p) => ({ ...p, audio: e.target.files?.[0] || null }))}
                className="mt-1 block w-full text-xs text-gray-300 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-pink-500 file:text-white hover:file:bg-pink-400"
                {...(!isEdit ? { required: true } : {})}
                data-testid="submit-beat-audio"
              />
              {files.audio && <p className="mt-1 text-[10px] text-emerald-300">✓ {files.audio.name}</p>}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Cover art (optional)</label>
              <input
                type="file" accept="image/*"
                onChange={(e) => onCover(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-xs text-gray-300 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-purple-500 file:text-white hover:file:bg-purple-400"
                data-testid="submit-beat-cover"
              />
              {coverPreview && (
                <img src={coverPreview} alt="Preview" className="mt-2 w-24 h-24 object-cover rounded-md border border-white/15" />
              )}
            </div>
          </Card>

          <Card className="p-4 bg-white/[0.02] border-white/10 space-y-3">
            <h2 className="text-sm font-black uppercase tracking-wider">Beat details</h2>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Title *</label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={120}
                     className="mt-1 bg-white/5 border-white/15 text-white" data-testid="submit-beat-title-input" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">BPM</label>
                <Input type="number" value={form.bpm} onChange={(e) => set("bpm", e.target.value)} min={40} max={300}
                       className="mt-1 bg-white/5 border-white/15 text-white" data-testid="submit-beat-bpm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Key</label>
                <select value={form.key} onChange={(e) => set("key", e.target.value)}
                        className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/15 text-white px-2 text-sm"
                        data-testid="submit-beat-key">
                  {KEYS.map((k) => <option key={k || "any"} value={k}>{k || "—"}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Genre</label>
                <Input value={form.genre} onChange={(e) => set("genre", e.target.value)} maxLength={40}
                       placeholder="Trap, Drill…" className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Mood</label>
                <Input value={form.mood} onChange={(e) => set("mood", e.target.value)} maxLength={40}
                       placeholder="Dark, Hype…" className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Tags (comma-separated)</label>
              <Input value={form.tags} onChange={(e) => set("tags", e.target.value)}
                     placeholder="808, dark, melodic" className="mt-1 bg-white/5 border-white/15 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Price (USD)</label>
                <Input type="number" min={0} step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)}
                       placeholder="0 for free" className="mt-1 bg-white/5 border-white/15 text-white"
                       data-testid="submit-beat-price" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">License</label>
                <select value={form.license_type} onChange={(e) => set("license_type", e.target.value)}
                        className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/15 text-white px-2 text-sm"
                        data-testid="submit-beat-license">
                  {LICENSES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white/[0.02] border-white/10 space-y-3" data-testid="tier-pricing-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-sm font-black uppercase tracking-wider">Marketplace pricing</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">Use simple pricing above, or enable multi-tier for Lease / Premium / Exclusive options.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={useTiers} onChange={(e) => setUseTiers(e.target.checked)} data-testid="use-tiers-toggle" />
                <span className="text-xs font-bold text-purple-200">Enable multi-tier pricing</span>
              </label>
            </div>
            {useTiers && (
              <div className="space-y-3" data-testid="tier-config-list">
                <p className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                  💰 RGM keeps 10% (intro). You receive the rest in your producer wallet after each sale, payable on request.
                </p>
                {tiers.map((t) => (
                  <div key={t.label} className={`p-3 rounded-lg border ${t.enabled ? "bg-purple-500/5 border-purple-500/30" : "bg-black/30 border-white/10 opacity-60"}`}
                       data-testid={`tier-row-${t.label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={t.enabled}
                          onChange={(e) => updateTier(t.label, { enabled: e.target.checked })}
                          data-testid={`tier-enabled-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                        />
                        <span className="font-bold text-white">{t.label}</span>
                      </label>
                      {t.label !== "Free Preview" && (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400">USD</span>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={t.price_usd}
                            onChange={(e) => updateTier(t.label, { price_usd: Number(e.target.value) || 0 })}
                            disabled={!t.enabled}
                            className="w-24 h-8 bg-black/40 border-white/15 text-white text-sm"
                            data-testid={`tier-price-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                          />
                        </div>
                      )}
                    </div>
                    <Textarea
                      value={t.terms}
                      onChange={(e) => updateTier(t.label, { terms: e.target.value })}
                      disabled={!t.enabled}
                      maxLength={500}
                      rows={2}
                      className="mt-2 bg-black/30 border-white/10 text-xs text-gray-200"
                      placeholder="License terms shown to buyers"
                      data-testid={`tier-terms-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Phase 19.0 — Download options */}
          <Card className="p-4 bg-white/[0.02] border-white/10 space-y-3" data-testid="download-options-card">
            <div>
              <h2 className="text-sm font-black uppercase tracking-wider">Download options</h2>
              <p className="text-[11px] text-gray-400 mt-0.5">Pick how artists can get this beat. Doesn't affect streaming previews.</p>
            </div>
            <div className="space-y-2">
              {[
                { value: "streaming", label: "Streaming Only", desc: "No download button. Stream-only on the marketplace." },
                { value: "free", label: "Free Download", desc: "Public download button. Counts toward Trending / Most Downloaded." },
                { value: "paid", label: "Paid Download", desc: "Buyer must purchase a license tier to unlock the download." },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                    form.download_mode === opt.value
                      ? "bg-purple-500/15 border-purple-500/50"
                      : "bg-black/30 border-white/10 hover:border-purple-500/30"
                  }`}
                  data-testid={`download-mode-${opt.value}`}
                >
                  <input
                    type="radio"
                    name="download_mode"
                    value={opt.value}
                    checked={form.download_mode === opt.value}
                    onChange={(e) => set("download_mode", e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-white text-sm font-bold">{opt.label}</p>
                    <p className="text-[11px] text-gray-400">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="require-account-toggle">
              <input
                type="checkbox"
                checked={!!form.require_account_to_download}
                onChange={(e) => set("require_account_to_download", e.target.checked)}
                className="mt-1"
              />
              <span className="text-xs text-gray-200">
                <strong className="text-white">Require an account to download</strong>
                <span className="block text-[11px] text-gray-400">Recommended — grows your follower list. Anonymous visitors will be asked to sign in first.</span>
              </span>
            </label>
          </Card>

          <Card className="p-4 bg-white/[0.02] border-white/10 space-y-3">
            <h2 className="text-sm font-black uppercase tracking-wider">How artists contact you</h2>
            <p className="text-[11px] text-gray-400 -mt-1">Pick any combination. Buttons appear on your beat card.</p>
            {["contact_email", "contact_instagram", "contact_discord", "contact_website"].map((k) => {
              const label = { contact_email: "✉ Email", contact_instagram: "📷 Instagram", contact_discord: "🎮 Discord", contact_website: "🌐 Website" }[k];
              const ph = { contact_email: "you@studio.com", contact_instagram: "@yourhandle", contact_discord: "username#1234", contact_website: "https://yoursite.com" }[k];
              return (
                <div key={k}>
                  <label className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">{label}</label>
                  <Input value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} maxLength={200}
                         className="mt-1 bg-white/5 border-white/15 text-white" data-testid={`submit-beat-${k}`} />
                </div>
              );
            })}
          </Card>

          <Button type="submit" disabled={uploading}
                  className="w-full h-12 text-base bg-gradient-to-r from-pink-600 to-purple-600 text-white font-black"
                  data-testid="submit-beat-submit">
            {uploading ? (isEdit ? "Saving…" : "Uploading…") : (isEdit ? "💾 Save changes" : "🚀 Publish to Beats Marketplace")}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
};

export default SubmitBeatPage;
